<template>
  <div class="dashboard-container">
    <div class="header">
      <h2>体测管理系统</h2>
    </div>

    <!-- 内容主体 -->
    <div class="content">
      <!-- 体测资讯卡片 -->
      <el-card class="news-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <span>体测资讯</span>
          </div>
        </template>
        <div class="news-list">
          <el-empty v-if="latestNews.length === 0" description="暂无资讯数据" />
          <div 
            v-for="news in latestNews" 
            :key="news.id" 
            class="news-item"
            @click="viewNewsDetail(news.id)"
          >
            <div class="news-info">
              <div class="news-header">
                <h3 class="news-title">{{ news.title }}</h3>
                <el-icon class="pdf-icon"><Document /></el-icon>
              </div>
              <p class="news-date">{{ formatNewsDate(news.date) }}</p>
              <p class="news-content">{{ news.content || '点击下载相关PDF文档' }}</p>
              <div class="news-action-container">
                <el-button type="text" class="view-btn">查看详情并下载PDF</el-button>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 体测活动卡片 -->
      <el-card class="activity-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <span>体测活动</span>
          </div>
        </template>
        <div class="activity-list">
          <el-empty v-if="activities.length === 0" description="暂无活动数据" />
          <div 
            v-for="activity in activities" 
            :key="activity.id" 
            class="activity-item"
          >
            <div class="activity-info">
                <h3 class="activity-title">{{ activity.name }}</h3>
                <div class="date-status-container">
                  <p class="activity-date">{{ formatActivityDate(activity.startDate, activity.endDate) }}</p>
                  <p class="activity-location">{{ activity.location || '暂无地点信息' }}</p>
                  <span :class="['activity-status', getStatusClass(activity.status)]">
                    {{ activity.status }}
                  </span>
                </div>
              </div>
          </div>
        </div>
      </el-card>



    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElNotification } from 'element-plus';
import request from '@/utils/request';

// 引入图标
import { Document, Running, Jumping, Weightlifting, Breathe } from '@element-plus/icons-vue';

// 路由对象
const router = useRouter();

// 响应式数据
const latestNews = ref([]);
const activities = ref([]);
const recentScore = ref(null);

// 查看资讯详情并下载PDF
const viewNewsDetail = async (newsId) => {
  try {
    console.log('查看资讯详情并下载PDF，ID:', newsId);
    
    // 直接下载对应PDF文件
    if (newsId === 1) {
      downloadPdf('通知');
    } else if (newsId === 2) {
      downloadPdf('标准');
    } else if (newsId === 3) {
      downloadPdf('饮食指南');
    } else {
      // 如果是其他ID，尝试从列表中查找并下载
      const news = latestNews.value.find(item => item.id === newsId);
      if (news && news.filePath) {
        // 如果有filePath，使用filePath下载
        downloadPdfByPath(news.filePath, news.title + '.pdf');
      } else {
        ElMessage.warning('未找到对应的PDF文件');
      }
    }
    
  } catch (error) {
    console.error('下载PDF失败:', error);
    ElMessage.error('下载PDF失败，请重试');
  }
};

// 根据类型下载PDF
const downloadPdf = (type) => {
  try {
    let fileName = '';
    
    switch (type) {
      case '通知':
        fileName = '体测通知.pdf';
        break;
      case '标准':
        fileName = '体测评分标准.pdf';
        break;
      case '饮食指南':
        fileName = '健康饮食指南.pdf';
        break;
      default:
        console.error('未知的PDF类型');
        return;
    }
    
    // 使用后端API获取PDF文件
    // 通过后端统一管理文件存储和访问
    const filePath = `/api/docs/${fileName}`;
    
    console.log('尝试下载PDF文件:', filePath);
    
    // 创建下载链接
    const link = document.createElement('a');
    link.href = filePath;
    link.download = fileName;
    
    document.body.appendChild(link);
    
    setTimeout(() => {
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      ElMessage.success(`开始下载${fileName}`);
    }, 0);
    
  } catch (error) {
    console.error('下载PDF失败:', error);
    ElMessage.error(`无法下载PDF文件，请检查文件是否存在。`);
  }
};

// 根据文件路径下载PDF
const downloadPdfByPath = (filePath, fileName) => {
  try {
    console.log('根据路径下载PDF文件:', filePath);
    
    // 确保使用正确的后端路径
    let finalFilePath = filePath;
    // 如果filePath不是完整的URL，则添加后端基础URL
    if (!filePath.startsWith('http')) {
      // 根据后端配置，使用/api前缀
      // 由于前端有代理配置，我们直接使用相对路径
      if (filePath.startsWith('/api')) {
        // 已经是正确的路径格式
        finalFilePath = filePath;
      } else {
        // 确保路径格式正确，添加/api前缀
        const path = filePath.startsWith('/') ? filePath.substring(1) : filePath;
        finalFilePath = `/api/${path}`;
      }
    }
    
    console.log('最终下载路径:', finalFilePath);
    
    // 创建下载链接
    const link = document.createElement('a');
    link.href = finalFilePath;
    link.download = fileName || '资讯文件.pdf';
    
    // 添加到文档并触发点击
    document.body.appendChild(link);
    
    // 使用延迟确保链接被正确添加
    setTimeout(() => {
      console.log('触发下载点击事件');
      link.click();
      
      // 移除链接
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      ElMessage.success(`开始下载${fileName}`);
    }, 0);
    
  } catch (error) {
    console.error('下载PDF失败:', error);
    ElMessage.error(`无法下载PDF文件，请检查网络连接或文件是否存在。`);
  }
};

// 打印latestNews数据供调试
setTimeout(() => {
  console.log('Dashboard页面的latestNews数据:', latestNews.value);
}, 1000);

// 报名参加活动
const registerActivity = async (activityId) => {
  try {
    const response = await request.post('/student/activities/register', { activityId });
    if (response && (response.code === 200 || response.success)) {
      ElMessage.success('报名成功');
      // 刷新活动列表
      fetchActivities();
    } else {
      ElMessage.error(response.message || '报名失败');
    }
  } catch (error) {
    console.error('报名活动失败:', error);
    ElMessage.error('报名失败，请稍后重试');
  }
};

// 获取数据
const fetchDashboardData = async () => {
  try {
    // 尝试从后端API获取资讯数据
    const response = await request.get('/student/news');
    
    console.log('从后端获取到的资讯数据:', response);
    
    // 简化数据处理逻辑，直接使用后端返回的数据
    if (response && response.data) {
      let newsData = [];
      
      // 灵活处理各种可能的数据格式
      if (Array.isArray(response.data)) {
        newsData = response.data;
      } else if (response.data.newsList) {
        newsData = response.data.newsList;
      } else {
        // 尝试直接使用response.data，兼容后端可能的直接返回
        newsData = [response.data];
      }
      
      // 直接使用后端返回的数据，保留原始字段
      latestNews.value = newsData.map(news => ({
        ...news,
        id: news.id || news.resourceId || 0,
        date: news.date || news.publishTime || '',
        title: news.title || '',
        content: news.content || news.briefContent || '',
        filePath: news.filePath
      }));
      
      console.log('更新后的资讯数据:', latestNews.value);
    } else {
      console.log('后端未返回数据');
      latestNews.value = [];
    }
  } catch (error) {
    console.error('获取资讯数据失败:', error);
    // 错误时显示空数组，避免使用模拟数据
    latestNews.value = [];
    ElMessage.error('获取资讯数据失败，请稍后重试');
  }
};

// 获取活动数据
const fetchActivities = async () => {
  try {
    const response = await request.get('/student/activities');
    
    console.log('活动API响应数据:', response);
    
    if (response && response.data) {
      // 处理响应数据，确保它是一个数组
      const data = Array.isArray(response.data) ? response.data : [];
      
      // 按状态排序：进行中 > 未开始 > 已结束
      const sortedData = [...data].sort((a, b) => {
        // 定义状态优先级
        const statusPriority = {
          '进行中': 3,
          '未开始': 2,
          '已结束': 1
        };
        // 获取状态优先级值，默认为0（未知状态排在最后）
        const priorityA = statusPriority[a.status] || 0;
        const priorityB = statusPriority[b.status] || 0;
        // 降序排列（优先级高的在前）
        return priorityB - priorityA;
      });
      
      activities.value = sortedData;
      
      console.log('处理并排序后的活动数据:', activities.value);
    } else {
      console.log('未获取到有效的活动数据');
      activities.value = [];
    }
  } catch (error) {
    console.error('获取活动数据失败:', error);
    activities.value = [];
  }
};

// 格式化活动日期
const formatActivityDate = (startDate, endDate) => {
  if (!startDate) return '';
  
  try {
    // 处理开始日期
    const start = new Date(startDate);
    const formattedStart = `${start.getFullYear()}-${(start.getMonth() + 1).toString().padStart(2, '0')}-${start.getDate().toString().padStart(2, '0')}`;
    
    // 如果有结束日期，添加结束日期
    if (endDate) {
      const end = new Date(endDate);
      const formattedEnd = `${end.getFullYear()}-${(end.getMonth() + 1).toString().padStart(2, '0')}-${end.getDate().toString().padStart(2, '0')}`;
      
      // 如果开始和结束日期不同，显示完整范围
      if (formattedStart !== formattedEnd) {
        return `${formattedStart} 至 ${formattedEnd}`;
      }
    }
    
    return formattedStart;
  } catch (error) {
    console.error('日期格式化错误:', error);
    return startDate; // 如果格式化失败，返回原始值
  }
};

// 获取状态类名
const getStatusClass = (status) => {
  switch (status) {
    case '进行中':
      return 'status-ongoing';
    case '已结束':
      return 'status-ended';
    case '未开始':
      return 'status-not-started';
    default:
      return '';
  }
};



// 获取等级类名
const getGradeClass = (grade) => {
  switch (grade) {
    case '优秀':
      return 'excellent';
    case '良好':
      return 'good';
    case '及格':
      return 'pass';
    case '不及格':
      return 'fail';
    default:
      return '';
  }
};

// 格式化资讯日期为年月日
const formatNewsDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    // 检查是否包含时间，如果包含则只保留日期部分
    if (dateString.includes(' ')) {
      return dateString.split(' ')[0];
    }
    // 检查是否包含T（ISO格式），如果包含则只保留日期部分
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    // 如果已经是日期格式，直接返回
    return dateString;
  } catch (error) {
    console.error('日期格式化错误:', error);
    return dateString;
  }
};

// 查看所有资讯
const viewAllNews = () => {
  router.push('/student/profile');
};

// 查看所有活动
const viewAllActivities = () => {
  router.push('/student/profile?tab=activities');
};

// 查看详细成绩
const viewDetailedScores = () => {
  router.push('/student/records');
};

// 组件挂载时获取数据
onMounted(() => {
  fetchDashboardData();
  fetchActivities(); // 调用获取活动数据的函数
});
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
  background-color: #f4f4f5;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.header {
  margin-bottom: 20px;
  width: 100%;
  max-width: 100%;
}

.header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
  max-width: 100%;
}

/* 体测资讯和体测活动左右平分显示 */
.news-card {
  grid-column: 1;
}

.activity-card {
    grid-column: 2;
  }

/* 如果有其他卡片需要显示，可以在这里添加样式 */

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.view-more-btn {
  font-size: 14px;
  color: #606266;
}

/* 资讯列表样式 */
.news-list {
  padding: 10px 0;
}

.news-item {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  background-color: #ffffff;
  border: 1px solid #ebeef5;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 120px;
}

.news-item:hover {
  background-color: #409eff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
}

.news-info {
  width: 100%;
}

.news-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.news-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0;
  flex: 1;
  transition: color 0.3s ease;
}

.news-item:hover .news-title {
  color: #ffffff;
}

.news-date {
  font-size: 12px;
  color: #909399;
  margin: 0 0 10px 0;
  transition: color 0.3s ease;
}

.news-item:hover .news-date {
  color: rgba(255, 255, 255, 0.8);
}

.news-content {
  font-size: 14px;
  color: #606266;
  margin: 0 0 12px 0;
  padding: 8px 0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  line-height: 1.5;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  transition: color 0.3s ease, border-color 0.3s ease;
}

.news-item:hover .news-content {
  color: rgba(255, 255, 255, 0.9);
  border-color: rgba(255, 255, 255, 0.2);
}

.news-action-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.pdf-icon {
  color: #f56c6c;
  margin-left: 8px;
  transition: color 0.3s ease;
  font-size: 20px;
}

.news-item:hover .pdf-icon {
  color: #ffffff;
}

.view-btn {
  font-size: 14px;
  color: #409eff;
  transition: color 0.3s ease;
}

.news-item:hover .view-btn {
  color: #ffffff;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .news-item {
    padding: 12px;
  }
  
  .news-title {
    font-size: 16px;
  }
  
  .news-content {
    -webkit-line-clamp: 2;
    font-size: 13px;
  }
}

/* 活动列表样式 */
.activity-list {
  padding: 10px 0;
}

.activity-item {
  padding: 12px 0;
  border-bottom: 1px solid #ebeef5;
}

.activity-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.activity-title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.date-status-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.activity-date {
  font-size: 12px;
  color: #909399;
  margin: 0;
  white-space: nowrap;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-location {
  font-size: 14px;
  color: #606266;
  margin: 0;
  white-space: nowrap;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  display: inline-block;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-ongoing {
  background-color: #f0f9eb;
  color: #67c23a;
}

.status-ended {
  background-color: #f5f7fa;
  color: #909399;
}

.status-not-started {
  background-color: #fdf6ec;
  color: #e6a23c;
}

/* 概览卡片样式 */
.overview-content {
  padding: 10px 0;
}

.score-display {
  text-align: center;
  padding: 20px 0;
}

.score-info {
  display: inline-block;
  text-align: left;
}

.score-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  min-width: 200px;
}

.score-label {
  font-size: 14px;
  color: #606266;
}

.score-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.grade-excellent {
  color: #67c23a;
}

.grade-good {
  color: #409eff;
}

.grade-pass {
  color: #e6a23c;
}

.grade-fail {
  color: #f56c6c;
}

.score-date {
  font-size: 12px;
  color: #909399;
}

/* 测试项目图标 */
.test-items {
  display: flex;
  justify-content: space-around;
  margin-top: 30px;
}

.test-item-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 10px;
  border-radius: 8px;
}

.test-item-icon:hover {
  background-color: #f5f7fa;
}

.test-item-icon .el-icon {
  font-size: 32px;
  color: #409eff;
  margin-bottom: 8px;
}

.test-item-icon span {
  font-size: 14px;
  color: #606266;
}
</style>